import { useState, useMemo, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { reportsApi, vehiclesApi } from '@/services/api';
import { useSettingsStore, formatDistance, formatSpeed } from '@/stores/settingsStore';
import OrganizationFilter from '@/components/OrganizationFilter';
import {
    DocumentTextIcon,
    ArrowDownTrayIcon,
    FunnelIcon,
    CalendarIcon,
    TruckIcon,
    BellAlertIcon,
    MapPinIcon,
    ClockIcon,
    ChartBarIcon,
    TableCellsIcon,
    ChartPieIcon,
    ArrowPathIcon,
    PrinterIcon,
    XCircleIcon,
    CurrencyDollarIcon,
    WrenchScrewdriverIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    FireIcon,
    GlobeAltIcon,
    BoltIcon,
    SignalIcon,
} from '@heroicons/react/24/outline';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Legend,
    AreaChart,
    Area,
    LineChart,
    Line,
    ComposedChart,
    RadarChart,
    Radar,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
} from 'recharts';
import dayjs from 'dayjs';
import clsx from 'clsx';

const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];
const MI_PER_KM = 0.621371;

type ReportType = 'overview' | 'vehicles' | 'alerts' | 'trips' | 'performance' | 'fuel' | 'maintenance';

interface DateRange {
    startDate: string;
    endDate: string;
}

const reportTypes: { id: ReportType; name: string; icon: React.ReactNode }[] = [
    { id: 'overview', name: 'نظرة عامة', icon: <ChartBarIcon className="w-5 h-5"/> },
    { id: 'vehicles', name: 'المركبات', icon: <TruckIcon className="w-5 h-5"/> },
    { id: 'alerts', name: 'التنبيهات', icon: <BellAlertIcon className="w-5 h-5"/> },
    { id: 'trips', name: 'الرحلات', icon: <MapPinIcon className="w-5 h-5"/> },
    { id: 'performance', name: 'الأداء', icon: <ChartPieIcon className="w-5 h-5"/> },
    { id: 'fuel', name: 'الوقود', icon: <CurrencyDollarIcon className="w-5 h-5"/> },
    { id: 'maintenance', name: 'الصيانة', icon: <WrenchScrewdriverIcon className="w-5 h-5"/> },
];

/**
 * A vehicle plate that navigates to its detail page.
 *
 * `Link`, never a bare `<a href>`: an anchor does a full document load, which
 * discards the loaded report and re-authenticates the whole app.
 */
function PlateLink({ id, plate }: { id?: string | null; plate?: string | null }) {
  if (!id || !plate) return <>{plate || '—'}</>;
  return (
    <Link
      to={`/vehicles/${id}`}
      className="text-primary-600 hover:text-primary-700 hover:underline font-medium"
      title="عرض تفاصيل المركبة"
    >
      {plate}
    </Link>
  );
}

const nf = new Intl.NumberFormat('en-US');
const fmtNum = (n: any) => nf.format(Math.round(Number(n) || 0));
const fmtDec = (n: any, d = 1) => (Number(n) || 0).toFixed(d);

/**
 * Every panel goes through this so a failed request shows the actual reason
 * instead of the old blanket "لا توجد بيانات" — which made a 500 or a 429
 * look identical to a genuinely empty range.
 */
function Panel({
    title, icon, subtitle, loading, error, empty, height = 300, children, actions,
}: {
    title: string;
    icon?: React.ReactNode;
    subtitle?: string;
    loading?: boolean;
    error?: unknown;
    empty?: boolean;
    height?: number;
    children: React.ReactNode;
    actions?: React.ReactNode;
}) {
    return (
        <div className="card">
            <div className="card-header flex items-start justify-between gap-2">
                <div>
                    <h3 className="card-title flex items-center gap-2">{icon}{title}</h3>
                    {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
                </div>
                {actions}
            </div>
            <div className="card-body">
                {loading ? (
                    <div className="flex items-center justify-center text-gray-400" style={{ height }}>
                        <div className="spinner w-8 h-8"/>
                    </div>
                ) : error ? (
                    <div className="flex flex-col items-center justify-center gap-2 text-center px-4"
                         style={{ height }}>
                        <ExclamationTriangleIcon className="w-10 h-10 text-warning-400"/>
                        <p className="text-sm font-medium text-gray-700">تعذّر تحميل البيانات</p>
                        <p className="text-xs text-gray-500">
                            {(error as any)?.response?.data?.message
                                || (error as any)?.message
                                || 'خطأ غير معروف'}
                        </p>
                    </div>
                ) : empty ? (
                    <div className="flex flex-col items-center justify-center gap-2 text-gray-400"
                         style={{ height }}>
                        <ChartBarIcon className="w-10 h-10"/>
                        <p className="text-sm">لا توجد بيانات في هذه الفترة</p>
                    </div>
                ) : children}
            </div>
        </div>
    );
}

function StatCard({ label, value, unit, icon, tone = 'primary' }: {
    label: string; value: React.ReactNode; unit?: string; icon: React.ReactNode; tone?: string;
}) {
    return (
        <div className={`card bg-gradient-to-br from-${tone}-500 to-${tone}-600 text-white`}>
            <div className="card-body">
                <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                        <p className="text-white/80 text-sm truncate">{label}</p>
                        <p className="text-2xl sm:text-3xl font-bold">
                            {value}{unit && <span className="text-lg font-medium mr-1">{unit}</span>}
                        </p>
                    </div>
                    <div className="opacity-50 shrink-0">{icon}</div>
                </div>
            </div>
        </div>
    );
}

export default function Reports() {
    const queryClient = useQueryClient();
    const { distanceUnit, speedUnit } = useSettingsStore();
    const [selectedReport, setSelectedReport] = useState<ReportType>(
        () => (new URLSearchParams(window.location.search).get('tab') as ReportType) || 'overview',
    );
    const [dateRange, setDateRange] = useState<DateRange>(() => {
        // `?period=today` lets the dashboard's trips tile land on today's data.
        const period = new URLSearchParams(window.location.search).get('period');
        const days = period === 'today' ? 0 : period === 'month' ? 30 : 7;
        return {
            startDate: dayjs().subtract(days, 'day').format('YYYY-MM-DD'),
            endDate: dayjs().format('YYYY-MM-DD'),
        };
    });
    const [selectedVehicle, setSelectedVehicle] = useState<string>('');
    // Tab, period and entity come from the query string, so the dashboard
    // tiles can deep-link to e.g. today's trips for one entity.
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedOrg = searchParams.get('organizationId') || '';
    const setSelectedOrg = (v: string) => {
        const next = new URLSearchParams(searchParams);
        if (v) next.set('organizationId', v); else next.delete('organizationId');
        setSearchParams(next, { replace: true });
    };
    const [showFilters, setShowFilters] = useState(false);
    // Empty string = defer to the server's per-vehicle-type rates. Any entered
    // value overrides them uniformly across the report.
    const [fuelRate, setFuelRate] = useState<string>('');
    const [fuelPrice, setFuelPrice] = useState<string>('');

    const params = useMemo(() => ({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        vehicleId: selectedVehicle || undefined,
        organizationId: selectedOrg || undefined,
    }), [dateRange.startDate, dateRange.endDate, selectedVehicle, selectedOrg]);

    // Distances/speeds arrive from the API in km and km/h; convert once at the
    // display boundary so the user's unit preference reaches the charts too,
    // not just the tables.
    const toDist = useCallback(
        (km: number) => (distanceUnit === 'mi' ? km * MI_PER_KM : km),
        [distanceUnit],
    );
    const toSpeed = useCallback(
        (kmh: number) => (speedUnit === 'mph' ? kmh * MI_PER_KM : kmh),
        [speedUnit],
    );
    const distLabel = distanceUnit === 'mi' ? 'ميل' : 'كم';
    const speedLabel = speedUnit === 'mph' ? 'م/س' : 'كم/س';

    // Only the active tab's queries run. Firing all ten at once on every date
    // change is what pushed the page into the rate limiter.
    const on = (...tabs: ReportType[]) => tabs.includes(selectedReport);

    const vehiclesQ = useQuery({
        queryKey: ['vehicles'],
        queryFn: async () => (await vehiclesApi.getAll()).data.data,
        staleTime: 5 * 60_000,
    });

    const summaryQ = useQuery({
        queryKey: ['report-summary', params],
        queryFn: async () => (await reportsApi.getSummary(params)).data.data,
        enabled: on('overview', 'vehicles', 'trips', 'performance'),
        staleTime: 60_000,
    });

    const dailyQ = useQuery({
        queryKey: ['report-daily', params],
        queryFn: async () => (await reportsApi.getDailyActivity(params)).data.data,
        enabled: on('overview', 'trips', 'alerts'),
        staleTime: 60_000,
    });

    const hourlyQ = useQuery({
        queryKey: ['report-hourly', params],
        queryFn: async () => (await reportsApi.getHourlyDistribution(params)).data.data,
        enabled: on('trips'),
        staleTime: 60_000,
    });

    const vehicleTypesQ = useQuery({
        queryKey: ['report-vehicle-types', params],
        queryFn: async () => (await reportsApi.getVehicleTypes(params)).data.data,
        enabled: on('vehicles'),
        staleTime: 5 * 60_000,
    });

    const topVehiclesQ = useQuery({
        queryKey: ['report-top-vehicles', params],
        queryFn: async () => (await reportsApi.getTopVehicles({ ...params, limit: 50 })).data.data,
        enabled: on('vehicles', 'overview'),
        staleTime: 60_000,
    });

    const alertsQ = useQuery({
        queryKey: ['report-alerts', params],
        queryFn: async () => {
            const r = await reportsApi.getAlertsSummary(params);
            return { items: r.data.data ?? [], meta: r.data.meta ?? {} };
        },
        enabled: on('alerts'),
        staleTime: 60_000,
    });

    const tripsQ = useQuery({
        queryKey: ['report-trips', params],
        queryFn: async () => {
            const r = await reportsApi.getTrips({ ...params, limit: 200 });
            return { items: r.data.data ?? [], meta: r.data.meta ?? {} };
        },
        enabled: on('trips'),
        staleTime: 60_000,
    });

    const fuelQ = useQuery({
        queryKey: ['report-fuel', params, fuelRate, fuelPrice],
        queryFn: async () => {
            const r = await reportsApi.getFuel({
                ...params,
                // Empty means "use the per-type defaults"; a value overrides them.
                lPer100km: fuelRate === '' ? undefined : Number(fuelRate),
                pricePerL: fuelPrice === '' ? undefined : Number(fuelPrice),
            });
            return { items: r.data.data ?? [], meta: r.data.meta ?? {}, byVehicle: r.data.byVehicle ?? [] };
        },
        enabled: on('fuel'),
        staleTime: 60_000,
    });

    const maintenanceQ = useQuery({
        queryKey: ['report-maintenance', params],
        queryFn: async () => {
            const r = await reportsApi.getMaintenance(params);
            return { items: r.data.data ?? [], meta: r.data.meta ?? {} };
        },
        enabled: on('maintenance'),
        staleTime: 60_000,
    });

    const performanceQ = useQuery({
        queryKey: ['report-performance', params],
        queryFn: async () => {
            const r = await reportsApi.getPerformance(params);
            return { items: r.data.data ?? [], meta: r.data.meta ?? {} };
        },
        enabled: on('performance', 'overview'),
        staleTime: 60_000,
    });

    const summary = summaryQ.data;
    const daily: any[] = Array.isArray(dailyQ.data) ? dailyQ.data : [];
    const hourly: any[] = Array.isArray(hourlyQ.data) ? hourlyQ.data : [];
    const topVehicles: any[] = Array.isArray(topVehiclesQ.data) ? topVehiclesQ.data : [];
    const vehicleTypes: any[] = Array.isArray(vehicleTypesQ.data) ? vehicleTypesQ.data : [];

    // ── Chart-shaped data ────────────────────────────────────────────────
    // Labels use the actual date. The old chart keyed the X axis on an English
    // weekday abbreviation, so a range longer than a week produced repeated
    // "Mon"/"Tue" ticks that Recharts collapsed onto each other.
    const activityData = useMemo(() => daily.map((d) => ({
        label: dayjs(d.date).format('DD/MM'),
        full: `${d.day} ${dayjs(d.date).format('DD/MM')}`,
        trips: d.trips ?? 0,
        distance: Number(toDist(d.distance ?? 0).toFixed(1)),
        vehicles: d.activeVehicles ?? 0,
        hours: d.drivingHours ?? 0,
        alerts: d.alerts ?? 0,
        critical: d.criticalAlerts ?? 0,
        resolved: d.resolvedAlerts ?? 0,
    })), [daily, toDist]);

    const hourlyData = useMemo(() => hourly.map((h) => ({
        hour: `${h.hour}:00`,
        trips: h.trips ?? 0,
        samples: h.count ?? 0,
        distance: Number(toDist(h.distance ?? 0).toFixed(1)),
        avgSpeed: Math.round(toSpeed(h.avgSpeed ?? 0)),
    })), [hourly, toSpeed, toDist]);

    const vehicleTypesPie = useMemo(() => vehicleTypes.map((v, i) => ({
        name: v.name,
        value: Number(v.value) || 0,
        color: COLORS[i % COLORS.length],
    })), [vehicleTypes]);

    const topChartData = useMemo(() => topVehicles.slice(0, 12).map((v) => ({
        name: v.plate,
        distance: Number(toDist(v.distance ?? 0).toFixed(0)),
        trips: v.trips ?? 0,
    })), [topVehicles, toDist]);

    const alertsByType = useMemo(() => {
        const items = alertsQ.data?.items ?? [];
        return items.map((a: any, i: number) => ({
            name: a.name,
            value: a.count ?? 0,
            resolved: a.resolved ?? 0,
            color: COLORS[i % COLORS.length],
        }));
    }, [alertsQ.data]);

    const alertsBySeverity = useMemo(
        () => (alertsQ.data?.meta?.bySeverity ?? []).filter((s: any) => s.value > 0),
        [alertsQ.data],
    );

    const fuelData = useMemo(() => (fuelQ.data?.items ?? []).map((d: any) => ({
        label: dayjs(d.date).format('DD/MM'),
        distance: Number(toDist(d.distance ?? 0).toFixed(1)),
        fuel: d.fuel ?? 0,
        cost: d.cost ?? 0,
    })), [fuelQ.data, toDist]);

    // ── Export ───────────────────────────────────────────────────────────
    // Exports whatever the visible tab is showing. The old version always
    // wrote the daily-activity table regardless of the selected report.
    const currentTable = useCallback((): { headers: string[]; rows: (string | number)[][] } => {
        switch (selectedReport) {
            case 'vehicles':
                return {
                    headers: ['المركبة', 'النوع', 'المؤسسة', 'الرحلات', `المسافة (${distLabel})`,
                        `المسافة المسجّلة (${distLabel})`, `العدّاد الكلي (${distLabel})`,
                        'ساعات القيادة', `أقصى سرعة (${speedLabel})`, 'أيام النشاط', 'التنبيهات', 'الحرجة'],
                    rows: topVehicles.map((v) => [
                        v.plate, v.type, v.organization ?? '-', v.trips,
                        fmtDec(toDist(v.distance), 0),
                        v.initialOdometerKm != null ? fmtDec(toDist(v.initialOdometerKm), 0) : '-',
                        v.totalOdometerKm != null ? fmtDec(toDist(v.totalOdometerKm), 0) : '-',
                        v.drivingHours,
                        Math.round(toSpeed(v.maxSpeed)), v.activeDays, v.alerts, v.critical,
                    ]),
                };
            case 'alerts':
                return {
                    headers: ['النوع', 'العدد', 'تم حلها', 'حرج', 'عالي', 'متوسط', 'منخفض'],
                    rows: (alertsQ.data?.items ?? []).map((a: any) => [
                        a.name, a.count, a.resolved,
                        a.bySeverity?.critical ?? 0, a.bySeverity?.high ?? 0,
                        a.bySeverity?.medium ?? 0, a.bySeverity?.low ?? 0,
                    ]),
                };
            case 'trips':
                return {
                    headers: ['المركبة', 'البداية', 'النهاية', `المسافة (${distLabel})`,
                        'المدة (دقيقة)', `أقصى سرعة (${speedLabel})`, `متوسط السرعة (${speedLabel})`],
                    rows: (tripsQ.data?.items ?? []).map((t: any) => [
                        t.plateNumber,
                        dayjs(t.startedAt).format('YYYY-MM-DD HH:mm'),
                        dayjs(t.endedAt).format('YYYY-MM-DD HH:mm'),
                        fmtDec(toDist(t.distance)), t.durationMin,
                        Math.round(toSpeed(t.maxSpeed)), Math.round(toSpeed(t.avgSpeed)),
                    ]),
                };
            case 'fuel':
                return {
                    headers: ['المركبة', 'النوع', 'الرحلات', `المسافة (${distLabel})`,
                        'المعدل (لتر/100كم)', 'الوقود (لتر)', 'التكلفة (د.ل)'],
                    rows: (fuelQ.data?.byVehicle ?? []).map((v: any) => [
                        v.plateNumber, v.vehicleType, v.trips,
                        fmtDec(toDist(v.distance)), v.lPer100km, v.fuel, v.cost,
                    ]),
                };
            case 'maintenance':
                return {
                    headers: ['المركبة', 'النوع', 'آخر صيانة', 'الصيانة القادمة', 'الأيام المتبقية',
                        `العداد (${distLabel})`, 'الحالة'],
                    rows: (maintenanceQ.data?.items ?? []).map((m: any) => [
                        m.vehicle, m.vehicleType,
                        m.lastMaintenanceDate ? dayjs(m.lastMaintenanceDate).format('YYYY-MM-DD') : '-',
                        m.nextMaintenanceDate ? dayjs(m.nextMaintenanceDate).format('YYYY-MM-DD') : '-',
                        m.daysRemaining ?? '-', fmtDec(toDist(m.odometerKm), 0), m.statusAr,
                    ]),
                };
            case 'performance':
                return {
                    headers: ['المؤشر', 'القيمة (%)'],
                    rows: (performanceQ.data?.items ?? []).map((p: any) => [p.subject, p.value]),
                };
            default:
                return {
                    headers: ['التاريخ', 'اليوم', 'الرحلات', `المسافة (${distLabel})`,
                        'المركبات النشطة', 'ساعات القيادة', 'التنبيهات'],
                    rows: daily.map((d) => [
                        d.date, d.day, d.trips, fmtDec(toDist(d.distance)),
                        d.activeVehicles, d.drivingHours, d.alerts,
                    ]),
                };
        }
    }, [selectedReport, topVehicles, alertsQ.data, tripsQ.data, fuelQ.data,
        maintenanceQ.data, performanceQ.data, daily, toDist, toSpeed, distLabel, speedLabel]);

    const download = (blob: Blob, filename: string) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // Without this the object URL leaks for the lifetime of the tab.
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    const exportToCSV = () => {
        const { headers, rows } = currentTable();
        // Quote every cell: Arabic labels and timestamps both contain commas.
        const esc = (c: any) => `"${String(c ?? '').replace(/"/g, '""')}"`;
        const csv = [headers.map(esc).join(','), ...rows.map((r) => r.map(esc).join(','))].join('\r\n');
        download(
            new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }),
            `report_${selectedReport}_${dateRange.startDate}_${dateRange.endDate}.csv`,
        );
    };

    const exportToJSON = () => {
        const payload = {
            reportType: selectedReport,
            dateRange,
            vehicleId: selectedVehicle || null,
            units: { distance: distanceUnit, speed: speedUnit },
            summary,
            table: currentTable(),
            generatedAt: new Date().toISOString(),
        };
        download(
            new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }),
            `report_${selectedReport}_${dateRange.startDate}_${dateRange.endDate}.json`,
        );
    };

    const setQuickDate = (days: number) => setDateRange({
        startDate: dayjs().subtract(days, 'day').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
    });

    const handleRefresh = () => {
        queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith('report-') });
    };

    const anyLoading = [summaryQ, dailyQ, hourlyQ, vehicleTypesQ, topVehiclesQ,
        alertsQ, tripsQ, fuelQ, maintenanceQ, performanceQ].some((q) => q.isFetching);

    const tooltipStyle = {
        contentStyle: { direction: 'rtl' as const, borderRadius: 8, fontSize: 13 },
    };

    return (
        <div className="space-y-4 sm:space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <DocumentTextIcon className="w-6 h-6 sm:w-7 sm:h-7 text-primary-500"/>
                        التقارير والتحليلات
                    </h1>
                    <p className="text-sm text-gray-600 mt-1 flex items-center gap-2 flex-wrap">
                        <CalendarIcon className="w-4 h-4"/>
                        <span dir="ltr">{dateRange.startDate} → {dateRange.endDate}</span>
                        {selectedVehicle && <span className="badge badge-info">مركبة واحدة</span>}
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap print:hidden">
                    <button onClick={handleRefresh} disabled={anyLoading}
                            className="btn btn-outline flex items-center gap-2 text-sm disabled:opacity-50">
                        <ArrowPathIcon className={clsx('w-5 h-5', anyLoading && 'animate-spin')}/>
                        <span className="hidden sm:inline">تحديث</span>
                    </button>
                    <button onClick={() => setShowFilters(!showFilters)}
                            className={clsx('btn btn-outline flex items-center gap-2 text-sm',
                                showFilters && 'bg-primary-50 border-primary-300')}>
                        <FunnelIcon className="w-5 h-5"/>
                        <span className="hidden sm:inline">الفلاتر</span>
                    </button>
                    <button onClick={exportToCSV}
                            className="btn btn-outline flex items-center gap-2 text-sm">
                        <ArrowDownTrayIcon className="w-5 h-5"/>CSV
                    </button>
                    <button onClick={exportToJSON}
                            className="btn btn-outline flex items-center gap-2 text-sm">
                        <TableCellsIcon className="w-5 h-5"/>JSON
                    </button>
                    <button onClick={() => window.print()}
                            className="btn btn-primary flex items-center gap-2 text-sm">
                        <PrinterIcon className="w-5 h-5"/>
                        <span className="hidden sm:inline">طباعة</span>
                    </button>
                </div>
            </div>

            {/* Filters */}
            {showFilters && (
                <div className="card print:hidden">
                    <div className="card-body">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">من تاريخ</label>
                                <input type="date" value={dateRange.startDate} max={dateRange.endDate}
                                       onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                                       className="input"/>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">إلى تاريخ</label>
                                <input type="date" value={dateRange.endDate} min={dateRange.startDate}
                                       max={dayjs().format('YYYY-MM-DD')}
                                       onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                                       className="input"/>
                            </div>
                            <OrganizationFilter value={selectedOrg} onChange={setSelectedOrg} showCounts />
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">المركبة</label>
                                <select value={selectedVehicle} className="input"
                                        onChange={(e) => setSelectedVehicle(e.target.value)}>
                                    <option value="">جميع المركبات</option>
                                    {Array.isArray(vehiclesQ.data) && vehiclesQ.data.map((v: any) => (
                                        <option key={v.id} value={v.id}>{v.plateNumber}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">فترات سريعة</label>
                                <div className="flex gap-2 flex-wrap">
                                    {[[1, 'يوم'], [7, 'أسبوع'], [30, 'شهر'], [90, '3 أشهر']].map(([d, l]) => (
                                        <button key={String(l)} onClick={() => setQuickDate(Number(d))}
                                                className="btn btn-sm btn-outline">{l}</button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="flex flex-wrap gap-2 print:hidden">
                {reportTypes.map((r) => (
                    <button key={r.id} onClick={() => setSelectedReport(r.id)}
                            className={clsx(
                                'flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-all text-sm',
                                selectedReport === r.id
                                    ? 'bg-primary-500 text-white shadow-lg'
                                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200',
                            )}>
                        {r.icon}<span className="font-medium">{r.name}</span>
                    </button>
                ))}
            </div>

            <div className="space-y-6">
                {/* ── Overview ─────────────────────────────────────────── */}
                {selectedReport === 'overview' && (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <StatCard label="إجمالي الرحلات" tone="primary"
                                      value={fmtNum(summary?.totalTrips)}
                                      icon={<MapPinIcon className="w-12 h-12"/>}/>
                            <StatCard label="المسافة الإجمالية" tone="success"
                                      value={fmtNum(toDist(summary?.totalDistance ?? 0))} unit={distLabel}
                                      icon={<GlobeAltIcon className="w-12 h-12"/>}/>
                            <StatCard label="متوسط مدة الرحلة" tone="warning"
                                      value={fmtDec(summary?.avgTripDurationMin)} unit="د"
                                      icon={<ClockIcon className="w-12 h-12"/>}/>
                            <StatCard label="معدل الاستخدام" tone="purple"
                                      value={`${summary?.utilizationRate ?? 0}%`}
                                      icon={<ChartBarIcon className="w-12 h-12"/>}/>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {[
                                { l: 'ساعات القيادة', v: fmtDec(summary?.totalDrivingHours), u: 'س' },
                                { l: 'متوسط السرعة', v: fmtNum(toSpeed(summary?.avgSpeed ?? 0)), u: speedLabel },
                                { l: 'أقصى سرعة مسجلة', v: fmtNum(toSpeed(summary?.maxSpeed ?? 0)), u: speedLabel },
                                { l: 'مركبات أرسلت بيانات', v: `${summary?.reportingVehicles ?? 0}/${summary?.activeVehicles ?? 0}`, u: '' },
                            ].map((s) => (
                                <div key={s.l} className="card">
                                    <div className="card-body">
                                        <p className="text-gray-500 text-sm">{s.l}</p>
                                        <p className="text-2xl font-bold text-gray-900">
                                            {s.v} <span className="text-base font-medium text-gray-500">{s.u}</span>
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <Panel title="النشاط اليومي" loading={dailyQ.isLoading} error={dailyQ.error}
                                   empty={!activityData.some((d) => d.trips || d.distance)}
                                   subtitle="الرحلات والمسافة والمركبات النشطة لكل يوم">
                                <ResponsiveContainer width="100%" height={300}>
                                    <ComposedChart data={activityData}>
                                        <CartesianGrid strokeDasharray="3 3"/>
                                        <XAxis dataKey="label" fontSize={12}/>
                                        <YAxis yAxisId="left" orientation="right" fontSize={12}/>
                                        <YAxis yAxisId="right" orientation="left" fontSize={12}/>
                                        <Tooltip {...tooltipStyle}
                                                 labelFormatter={(_, p) => p?.[0]?.payload?.full ?? ''}/>
                                        <Legend/>
                                        <Bar yAxisId="left" dataKey="trips" name="الرحلات" fill="#3b82f6"
                                             radius={[4, 4, 0, 0]}/>
                                        <Line yAxisId="right" type="monotone" dataKey="distance"
                                              name={`المسافة (${distLabel})`} stroke="#22c55e" strokeWidth={2}
                                              dot={{ r: 3 }}/>
                                        <Line yAxisId="left" type="monotone" dataKey="vehicles"
                                              name="المركبات النشطة" stroke="#f59e0b" strokeWidth={2}
                                              strokeDasharray="4 4" dot={false}/>
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </Panel>

                            <Panel title="مؤشرات الأداء" icon={<ChartPieIcon className="w-5 h-5 text-primary-500"/>}
                                   loading={performanceQ.isLoading} error={performanceQ.error}
                                   empty={!(performanceQ.data?.items ?? []).length}
                                   subtitle="نِسب فعلية محسوبة من حجم الأسطول والعيّنات">
                                <ResponsiveContainer width="100%" height={300}>
                                    <RadarChart data={performanceQ.data?.items ?? []}>
                                        <PolarGrid/>
                                        <PolarAngleAxis dataKey="subject" fontSize={12}/>
                                        <PolarRadiusAxis angle={30} domain={[0, 100]} fontSize={11}/>
                                        <Radar name="الأداء %" dataKey="value" stroke="#3b82f6"
                                               fill="#3b82f6" fillOpacity={0.45}/>
                                        <Tooltip {...tooltipStyle} formatter={(v: any) => [`${v}%`, 'القيمة']}/>
                                    </RadarChart>
                                </ResponsiveContainer>
                            </Panel>
                        </div>

                        <Panel title="أعلى المركبات نشاطاً" icon={<TruckIcon className="w-5 h-5 text-primary-500"/>}
                               loading={topVehiclesQ.isLoading} error={topVehiclesQ.error}
                               empty={!topChartData.length}>
                            <ResponsiveContainer width="100%" height={Math.max(240, topChartData.length * 34)}>
                                <BarChart data={topChartData} layout="vertical"
                                          margin={{ right: 90, left: 10 }}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false}/>
                                    <XAxis type="number" fontSize={12}/>
                                    <YAxis dataKey="name" type="category" orientation="right"
                                           width={90} fontSize={12}/>
                                    <Tooltip {...tooltipStyle}/>
                                    <Legend/>
                                    <Bar dataKey="distance" name={`المسافة (${distLabel})`}
                                         fill="#22c55e" radius={[0, 4, 4, 0]}/>
                                    <Bar dataKey="trips" name="الرحلات" fill="#3b82f6" radius={[0, 4, 4, 0]}/>
                                </BarChart>
                            </ResponsiveContainer>
                        </Panel>
                    </>
                )}

                {/* ── Vehicles ─────────────────────────────────────────── */}
                {selectedReport === 'vehicles' && (
                    <>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <Panel title="توزيع أنواع المركبات"
                                   icon={<ChartPieIcon className="w-5 h-5 text-primary-500"/>}
                                   loading={vehicleTypesQ.isLoading} error={vehicleTypesQ.error}
                                   empty={!vehicleTypesPie.length}>
                                <ResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                        <Pie data={vehicleTypesPie} cx="50%" cy="50%" outerRadius={100}
                                             dataKey="value"
                                             label={({ name, value }) => `${name}: ${value}`}>
                                            {vehicleTypesPie.map((e, i) => (
                                                <Cell key={i} fill={e.color}/>
                                            ))}
                                        </Pie>
                                        <Tooltip {...tooltipStyle}/>
                                        <Legend/>
                                    </PieChart>
                                </ResponsiveContainer>
                            </Panel>

                            <Panel title="المسافة مقابل الرحلات"
                                   icon={<TruckIcon className="w-5 h-5 text-primary-500"/>}
                                   loading={topVehiclesQ.isLoading} error={topVehiclesQ.error}
                                   empty={!topChartData.length}>
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={topChartData} layout="vertical"
                                              margin={{ right: 80, left: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" horizontal={false}/>
                                        <XAxis type="number" fontSize={12}/>
                                        <YAxis dataKey="name" type="category" orientation="right"
                                               width={80} fontSize={12}/>
                                        <Tooltip {...tooltipStyle}/>
                                        <Legend/>
                                        <Bar dataKey="trips" name="الرحلات" fill="#3b82f6"/>
                                        <Bar dataKey="distance" name={`المسافة (${distLabel})`} fill="#22c55e"/>
                                    </BarChart>
                                </ResponsiveContainer>
                            </Panel>
                        </div>

                        <Panel title="تفاصيل المركبات" height={200}
                               loading={topVehiclesQ.isLoading} error={topVehiclesQ.error}
                               empty={!topVehicles.length}
                               subtitle={`${topVehicles.length} مركبة`}>
                            {/* Mobile cards */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:hidden">
                                {topVehicles.map((v) => (
                                    <div key={v.id} className="border border-gray-200 rounded-lg p-3">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="font-semibold">
                                                <PlateLink id={v.id} plate={v.plate} />
                                            </span>
                                            <span className={clsx('badge',
                                                v.critical > 0 ? 'badge-error' : 'badge-success')}>
                                                {v.critical} حرج
                                            </span>
                                        </div>
                                        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm text-gray-600">
                                            <div><dt className="inline text-gray-500">الرحلات: </dt>
                                                <dd className="inline font-medium">{v.trips}</dd></div>
                                            <div><dt className="inline text-gray-500">المسافة: </dt>
                                                <dd className="inline font-medium">
                                                    {formatDistance(v.distance, distanceUnit)}</dd></div>
                                            <div><dt className="inline text-gray-500">القيادة: </dt>
                                                <dd className="inline font-medium">{v.drivingHours} س</dd></div>
                                            <div><dt className="inline text-gray-500">أقصى سرعة: </dt>
                                                <dd className="inline font-medium">
                                                    {formatSpeed(v.maxSpeed, speedUnit)}</dd></div>
                                            <div><dt className="inline text-gray-500">أيام النشاط: </dt>
                                                <dd className="inline font-medium">{v.activeDays}</dd></div>
                                            <div><dt className="inline text-gray-500">التنبيهات: </dt>
                                                <dd className="inline font-medium">{v.alerts}</dd></div>
                                            <div><dt className="inline text-gray-500">المسافة المسجّلة: </dt>
                                                <dd className="inline font-medium">
                                                    {v.initialOdometerKm != null
                                                        ? formatDistance(v.initialOdometerKm, distanceUnit) : '—'}</dd></div>
                                            <div><dt className="inline text-gray-500">العدّاد الكلي: </dt>
                                                <dd className="inline font-medium">
                                                    {v.totalOdometerKm != null
                                                        ? formatDistance(v.totalOdometerKm, distanceUnit) : '—'}</dd></div>
                                        </dl>
                                    </div>
                                ))}
                            </div>
                            {/* Desktop table */}
                            <div className="hidden lg:block overflow-x-auto">
                                <table className="table">
                                    <thead>
                                    <tr>
                                        <th>المركبة</th><th>النوع</th><th>المؤسسة</th><th>الرحلات</th>
                                        <th>المسافة</th><th>المسافة المسجّلة</th><th>العدّاد الكلي</th>
                                        <th>ساعات القيادة</th><th>أقصى سرعة</th>
                                        <th>أيام النشاط</th><th>التنبيهات</th><th>الحرجة</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {topVehicles.map((v) => (
                                        <tr key={v.id}>
                                            <td className="font-medium"><PlateLink id={v.id} plate={v.plate} /></td>
                                            <td>{v.type}</td>
                                            <td className="text-gray-500">{v.organization || '—'}</td>
                                            <td>{v.trips}</td>
                                            <td>{formatDistance(v.distance, distanceUnit)}</td>
                                            <td>{v.initialOdometerKm != null
                                                ? formatDistance(v.initialOdometerKm, distanceUnit) : '—'}</td>
                                            <td>
                                                {v.totalOdometerKm != null
                                                    ? formatDistance(v.totalOdometerKm, distanceUnit) : '—'}
                                                {/* Says whether the mileage since the recorded
                                                    reading was measured by the tracker or derived
                                                    from the GPS track. */}
                                                {v.odometerSource === 'gps' && (
                                                    <span className="text-xs text-gray-400 mr-1">(GPS)</span>
                                                )}
                                            </td>
                                            <td>{v.drivingHours} س</td>
                                            <td>{formatSpeed(v.maxSpeed, speedUnit)}</td>
                                            <td>{v.activeDays}</td>
                                            <td>{v.alerts}</td>
                                            <td>
                                                <span className={clsx('badge',
                                                    v.critical > 0 ? 'badge-error' : 'badge-success')}>
                                                    {v.critical}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        </Panel>
                    </>
                )}

                {/* ── Alerts ───────────────────────────────────────────── */}
                {selectedReport === 'alerts' && (
                    <>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            {[
                                { l: 'إجمالي التنبيهات', v: alertsQ.data?.meta?.total ?? 0,
                                    c: 'text-gray-900', b: 'border-r-gray-500',
                                    i: <ExclamationTriangleIcon className="w-5 h-5 text-gray-500"/> },
                                { l: 'التنبيهات الحرجة',
                                    v: alertsBySeverity.find((s: any) => s.key === 'critical')?.value ?? 0,
                                    c: 'text-emergency-600', b: 'border-r-emergency-500',
                                    i: <XCircleIcon className="w-5 h-5 text-emergency-500"/> },
                                { l: 'تم حلها', v: alertsQ.data?.meta?.resolved ?? 0,
                                    c: 'text-success-600', b: 'border-r-success-500',
                                    i: <CheckCircleIcon className="w-5 h-5 text-success-500"/> },
                                { l: 'معدل الحل', v: `${alertsQ.data?.meta?.resolutionRate ?? 0}%`,
                                    c: 'text-primary-600', b: 'border-r-primary-500',
                                    i: <ChartBarIcon className="w-5 h-5 text-primary-500"/> },
                            ].map((s) => (
                                <div key={s.l} className={`card border-r-4 ${s.b}`}>
                                    <div className="card-body">
                                        <div className="flex items-center gap-2">
                                            {s.i}<p className="text-gray-500 text-xs sm:text-sm">{s.l}</p>
                                        </div>
                                        <p className={`text-2xl font-bold ${s.c}`}>{fmtNum(s.v) === 'NaN'
                                            ? s.v : (typeof s.v === 'string' ? s.v : fmtNum(s.v))}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <Panel title="التنبيهات حسب الخطورة"
                                   icon={<FireIcon className="w-5 h-5 text-emergency-500"/>}
                                   loading={alertsQ.isLoading} error={alertsQ.error}
                                   empty={!alertsBySeverity.length}>
                                <ResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                        <Pie data={alertsBySeverity} cx="50%" cy="50%" innerRadius={60}
                                             outerRadius={100} dataKey="value" nameKey="name"
                                             label={({ name, value }) => `${name}: ${value}`}>
                                            {alertsBySeverity.map((e: any, i: number) => (
                                                <Cell key={i} fill={e.color}/>
                                            ))}
                                        </Pie>
                                        <Tooltip {...tooltipStyle}/>
                                        <Legend/>
                                    </PieChart>
                                </ResponsiveContainer>
                            </Panel>

                            {/* Replaces the old Treemap, which rendered unlabelled blocks
                                because it had no content renderer and ignored per-cell fill. */}
                            <Panel title="التنبيهات حسب النوع"
                                   icon={<BoltIcon className="w-5 h-5 text-warning-500"/>}
                                   loading={alertsQ.isLoading} error={alertsQ.error}
                                   empty={!alertsByType.length}>
                                <ResponsiveContainer width="100%"
                                                     height={Math.max(300, alertsByType.length * 32)}>
                                    <BarChart data={alertsByType} layout="vertical"
                                              margin={{ right: 110, left: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" horizontal={false}/>
                                        <XAxis type="number" fontSize={12}/>
                                        <YAxis dataKey="name" type="category" orientation="right"
                                               width={110} fontSize={12}/>
                                        <Tooltip {...tooltipStyle}/>
                                        <Legend/>
                                        <Bar dataKey="value" name="العدد" radius={[0, 4, 4, 0]}>
                                            {alertsByType.map((e: any, i: number) => (
                                                <Cell key={i} fill={e.color}/>
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </Panel>
                        </div>

                        <Panel title="اتجاه التنبيهات اليومي" loading={dailyQ.isLoading} error={dailyQ.error}
                               empty={!activityData.some((d) => d.alerts)}>
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={activityData}>
                                    <CartesianGrid strokeDasharray="3 3"/>
                                    <XAxis dataKey="label" fontSize={12}/>
                                    <YAxis orientation="right" fontSize={12} allowDecimals={false}/>
                                    <Tooltip {...tooltipStyle}
                                             labelFormatter={(_, p) => p?.[0]?.payload?.full ?? ''}/>
                                    <Legend/>
                                    <Line type="monotone" dataKey="alerts" name="الإجمالي" stroke="#3b82f6"
                                          strokeWidth={2} dot={{ r: 3 }}/>
                                    <Line type="monotone" dataKey="critical" name="الحرجة" stroke="#dc2626"
                                          strokeWidth={2} dot={{ r: 3 }}/>
                                    <Line type="monotone" dataKey="resolved" name="تم حلها" stroke="#22c55e"
                                          strokeWidth={2} dot={{ r: 3 }}/>
                                </LineChart>
                            </ResponsiveContainer>
                        </Panel>
                    </>
                )}

                {/* ── Trips ────────────────────────────────────────────── */}
                {selectedReport === 'trips' && (
                    <>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            {[
                                { l: 'عدد الرحلات', v: fmtNum(summary?.totalTrips) },
                                { l: 'إجمالي المسافة', v: `${fmtNum(toDist(summary?.totalDistance ?? 0))} ${distLabel}` },
                                { l: 'متوسط مسافة الرحلة', v: `${fmtDec(toDist(summary?.avgTripDistance ?? 0))} ${distLabel}` },
                                { l: 'إجمالي ساعات القيادة', v: `${fmtDec(summary?.totalDrivingHours)} س` },
                            ].map((s) => (
                                <div key={s.l} className="card">
                                    <div className="card-body">
                                        <p className="text-gray-500 text-xs sm:text-sm">{s.l}</p>
                                        <p className="text-xl sm:text-2xl font-bold text-gray-900">{s.v}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <Panel title="التوزيع الساعي للرحلات"
                                   subtitle="عدد الرحلات التي بدأت في كل ساعة (توقيت طرابلس)"
                                   loading={hourlyQ.isLoading} error={hourlyQ.error}
                                   empty={!hourlyData.some((h) => h.trips || h.distance)}>
                                <ResponsiveContainer width="100%" height={300}>
                                    <AreaChart data={hourlyData}>
                                        <CartesianGrid strokeDasharray="3 3"/>
                                        <XAxis dataKey="hour" interval={1} fontSize={11} angle={-45}
                                               textAnchor="end" height={50}/>
                                        <YAxis orientation="right" fontSize={12} allowDecimals={false}/>
                                        <Tooltip {...tooltipStyle}/>
                                        <Legend/>
                                        <Area type="monotone" dataKey="trips" name="الرحلات" fill="#3b82f6"
                                              fillOpacity={0.5} stroke="#3b82f6"/>
                                    </AreaChart>
                                </ResponsiveContainer>
                            </Panel>

                            <Panel title="المسافة اليومية" loading={dailyQ.isLoading} error={dailyQ.error}
                                   empty={!activityData.some((d) => d.distance)}>
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={activityData}>
                                        <CartesianGrid strokeDasharray="3 3"/>
                                        <XAxis dataKey="label" fontSize={12}/>
                                        <YAxis orientation="right" fontSize={12}/>
                                        <Tooltip {...tooltipStyle}
                                                 labelFormatter={(_, p) => p?.[0]?.payload?.full ?? ''}/>
                                        <Bar dataKey="distance" name={`المسافة (${distLabel})`}
                                             fill="#22c55e" radius={[4, 4, 0, 0]}/>
                                    </BarChart>
                                </ResponsiveContainer>
                            </Panel>
                        </div>

                        <Panel title="سجل الرحلات" height={200}
                               icon={<MapPinIcon className="w-5 h-5 text-primary-500"/>}
                               subtitle="الرحلات مُستخرجة من بيانات المواقع: حركة متصلة تفصلها وقفات تزيد عن 5 دقائق"
                               loading={tripsQ.isLoading} error={tripsQ.error}
                               empty={!(tripsQ.data?.items ?? []).length}
                               actions={tripsQ.data?.meta?.total ? (
                                   <span className="badge badge-info shrink-0">
                                       {fmtNum(tripsQ.data.meta.total)} رحلة
                                   </span>
                               ) : undefined}>
                            <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
                                <table className="table">
                                    <thead className="sticky top-0 bg-white z-10">
                                    <tr>
                                        <th>المركبة</th><th>البداية</th><th>النهاية</th>
                                        <th>المدة</th><th>المسافة</th><th>أقصى سرعة</th><th>متوسط السرعة</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {(tripsQ.data?.items ?? []).map((t: any, i: number) => (
                                        <tr key={i}>
                                            <td className="font-medium whitespace-nowrap">
                                                <PlateLink id={t.vehicleId} plate={t.plateNumber} />
                                            </td>
                                            <td className="whitespace-nowrap text-sm" dir="ltr">
                                                {dayjs(t.startedAt).format('DD/MM HH:mm')}</td>
                                            <td className="whitespace-nowrap text-sm" dir="ltr">
                                                {dayjs(t.endedAt).format('DD/MM HH:mm')}</td>
                                            <td className="whitespace-nowrap">{t.durationMin} د</td>
                                            <td className="whitespace-nowrap">
                                                {formatDistance(t.distance, distanceUnit)}</td>
                                            <td className="whitespace-nowrap">
                                                {formatSpeed(t.maxSpeed, speedUnit)}</td>
                                            <td className="whitespace-nowrap">
                                                {formatSpeed(t.avgSpeed, speedUnit)}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        </Panel>
                    </>
                )}

                {/* ── Performance ──────────────────────────────────────── */}
                {selectedReport === 'performance' && (
                    <>
                        <Panel title="مؤشرات الأداء الرئيسية" height={400}
                               icon={<ChartPieIcon className="w-5 h-5 text-primary-500"/>}
                               subtitle="كل مؤشر نسبة حقيقية من 100 محسوبة مقابل حجم الأسطول وعدد العيّنات"
                               loading={performanceQ.isLoading} error={performanceQ.error}
                               empty={!(performanceQ.data?.items ?? []).length}>
                            <ResponsiveContainer width="100%" height={400}>
                                <RadarChart data={performanceQ.data?.items ?? []}>
                                    <PolarGrid/>
                                    <PolarAngleAxis dataKey="subject" fontSize={13}/>
                                    <PolarRadiusAxis angle={30} domain={[0, 100]} fontSize={11}/>
                                    <Radar name="الأداء %" dataKey="value" stroke="#3b82f6"
                                           fill="#3b82f6" fillOpacity={0.45}/>
                                    <Tooltip {...tooltipStyle} formatter={(v: any) => [`${v}%`, 'القيمة']}/>
                                    <Legend/>
                                </RadarChart>
                            </ResponsiveContainer>
                        </Panel>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {(performanceQ.data?.items ?? []).map((p: any) => (
                                <div key={p.key} className="card">
                                    <div className="card-body">
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="text-gray-600 text-sm font-medium">{p.subject}</p>
                                            <span className={clsx('text-lg font-bold',
                                                p.value >= 75 ? 'text-success-600'
                                                    : p.value >= 45 ? 'text-warning-600'
                                                        : 'text-emergency-600')}>
                                                {p.value}%
                                            </span>
                                        </div>
                                        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                                            <div className={clsx('h-full rounded-full transition-all',
                                                p.value >= 75 ? 'bg-success-500'
                                                    : p.value >= 45 ? 'bg-warning-500' : 'bg-emergency-500')}
                                                 style={{ width: `${p.value}%` }}/>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {performanceQ.data?.meta && (
                            <Panel title="الأساس الحسابي" height={120}
                                   icon={<SignalIcon className="w-5 h-5 text-gray-500"/>}>
                                <dl className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 text-sm">
                                    {[
                                        ['حجم الأسطول', performanceQ.data.meta.activeFleet],
                                        ['مركبات أرسلت بيانات', performanceQ.data.meta.reportingVehicles],
                                        ['مركبات تحركت', performanceQ.data.meta.movingVehicles],
                                        ['عيّنات المواقع', fmtNum(performanceQ.data.meta.samples)],
                                        ['عيّنات تجاوزت السرعة', fmtNum(performanceQ.data.meta.overLimitSamples)],
                                        ['حد السرعة', `${performanceQ.data.meta.speedLimit} كم/س`],
                                        ['إجمالي التنبيهات', fmtNum(performanceQ.data.meta.alerts)],
                                        ['ساعات القيادة', performanceQ.data.meta.drivingHours],
                                    ].map(([l, v]) => (
                                        <div key={String(l)}>
                                            <dt className="text-gray-500 text-xs">{l}</dt>
                                            <dd className="font-semibold text-gray-900">{String(v)}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </Panel>
                        )}
                    </>
                )}

                {/* ── Fuel ─────────────────────────────────────────────── */}
                {selectedReport === 'fuel' && (
                    <>
                        {/* The formula is stated and its inputs are editable, so the
                            number is auditable rather than a black box. */}
                        <div className="card">
                            <div className="card-body">
                                <div className="flex flex-col lg:flex-row lg:items-end gap-4">
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-gray-700 mb-1">
                                            طريقة الحساب
                                        </p>
                                        <p className="text-sm text-gray-600 font-mono" dir="rtl">
                                            اللترات = (المسافة المقطوعة بالكيلومتر × معدل الاستهلاك لكل 100 كم) ÷ 100
                                        </p>
                                        <p className="text-sm text-gray-600 font-mono mt-1" dir="rtl">
                                            التكلفة = اللترات × سعر اللتر
                                        </p>
                                    </div>
                                    <div className="flex flex-wrap gap-3">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                معدل الاستهلاك (لتر/100كم)
                                            </label>
                                            <input type="number" min="0.1" max="100" step="0.5"
                                                   value={fuelRate} className="input w-40"
                                                   placeholder="حسب نوع المركبة"
                                                   onChange={(e) => setFuelRate(e.target.value)}/>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                سعر اللتر (د.ل)
                                            </label>
                                            <input type="number" min="0" step="0.05"
                                                   value={fuelPrice} className="input w-32"
                                                   placeholder={String(fuelQ.data?.meta?.pricePerL ?? 2.5)}
                                                   onChange={(e) => setFuelPrice(e.target.value)}/>
                                        </div>
                                        {(fuelRate !== '' || fuelPrice !== '') && (
                                            <button onClick={() => { setFuelRate(''); setFuelPrice(''); }}
                                                    className="btn btn-outline btn-sm self-end">
                                                استعادة الافتراضي
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Per-type defaults, shown only while no manual override is active. */}
                                {Array.isArray(fuelQ.data?.meta?.ratesByType)
                                    && fuelQ.data.meta.ratesByType.length > 0 && (
                                    <div className="mt-4 pt-4 border-t border-gray-100">
                                        <p className="text-xs text-gray-500 mb-2">
                                            المعدلات المستخدمة حسب نوع المركبة:
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {fuelQ.data.meta.ratesByType.map((r: any) => (
                                                <span key={r.type}
                                                      className="px-2.5 py-1 rounded-lg bg-gray-100 text-gray-700 text-xs">
                                                    {r.name}: <span className="font-semibold">{r.lPer100km}</span> لتر/100كم
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {fuelQ.data?.meta?.lPer100km && (
                                    <p className="mt-3 text-xs text-warning-700 bg-warning-50 border border-warning-200
                                                  rounded-lg px-3 py-2">
                                        معدل موحّد مطبَّق على كل المركبات:{' '}
                                        <span className="font-semibold">{fuelQ.data.meta.lPer100km}</span> لتر/100كم
                                    </p>
                                )}
                            </div>
                        </div>

                        <Panel title="تقدير استهلاك الوقود"
                               icon={<CurrencyDollarIcon className="w-5 h-5 text-warning-500"/>}
                               subtitle={`على المسافة الفعلية المقطوعة، بسعر ${fuelQ.data?.meta?.pricePerL ?? 2.5} د.ل/لتر`}
                               loading={fuelQ.isLoading} error={fuelQ.error}
                               empty={!fuelData.some((d: any) => d.fuel)}>
                            <ResponsiveContainer width="100%" height={320}>
                                <ComposedChart data={fuelData}>
                                    <CartesianGrid strokeDasharray="3 3"/>
                                    <XAxis dataKey="label" fontSize={12}/>
                                    <YAxis yAxisId="left" orientation="right" fontSize={12}/>
                                    <YAxis yAxisId="right" orientation="left" fontSize={12}/>
                                    <Tooltip {...tooltipStyle}/>
                                    <Legend/>
                                    <Bar yAxisId="left" dataKey="fuel" name="الوقود (لتر)" fill="#f59e0b"
                                         radius={[4, 4, 0, 0]}/>
                                    <Line yAxisId="right" type="monotone" dataKey="cost"
                                          name="التكلفة (د.ل)" stroke="#ef4444" strokeWidth={2}
                                          dot={{ r: 3 }}/>
                                </ComposedChart>
                            </ResponsiveContainer>
                        </Panel>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {[
                                { l: 'إجمالي الوقود المقدر', v: `${fmtDec(fuelQ.data?.meta?.totalFuel)} لتر`,
                                    bg: 'bg-warning-50 border-warning-200', tx: 'text-warning-700' },
                                { l: 'إجمالي التكلفة المقدرة', v: `${fmtNum(fuelQ.data?.meta?.totalCost)} د.ل`,
                                    bg: 'bg-emergency-50 border-emergency-200', tx: 'text-emergency-700' },
                                { l: 'متوسط الاستهلاك اليومي', v: `${fmtDec(fuelQ.data?.meta?.avgDailyFuel)} لتر`,
                                    bg: 'bg-primary-50 border-primary-200', tx: 'text-primary-700' },
                                { l: 'المسافة المقطوعة',
                                    v: `${fmtNum(toDist(fuelQ.data?.meta?.totalDistance ?? 0))} ${distLabel}`,
                                    bg: 'bg-success-50 border-success-200', tx: 'text-success-700' },
                            ].map((c) => (
                                <div key={c.l} className={`card border ${c.bg}`}>
                                    <div className="card-body text-center">
                                        <p className="text-sm text-gray-600">{c.l}</p>
                                        <p className={`text-2xl font-bold ${c.tx}`}>{c.v}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <Panel title="الاستهلاك حسب المركبة" height={200}
                               icon={<TruckIcon className="w-5 h-5 text-warning-500"/>}
                               subtitle={`المعدل الفعلي للأسطول في هذه الفترة: ${fmtDec(fuelQ.data?.meta?.avgLPer100km)} لتر/100كم`}
                               loading={fuelQ.isLoading} error={fuelQ.error}
                               empty={!(fuelQ.data?.byVehicle ?? []).length}>
                            <div className="overflow-x-auto">
                                <table className="table">
                                    <thead>
                                    <tr>
                                        <th>المركبة</th><th>النوع</th><th>الرحلات</th><th>المسافة</th>
                                        <th>المعدل (لتر/100كم)</th><th>الوقود (لتر)</th><th>التكلفة (د.ل)</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {(fuelQ.data?.byVehicle ?? []).map((v: any) => (
                                        <tr key={v.vehicleId}>
                                            <td className="font-medium"><PlateLink id={v.vehicleId} plate={v.plateNumber} /></td>
                                            <td>{v.vehicleType}</td>
                                            <td>{v.trips}</td>
                                            <td>{formatDistance(v.distance, distanceUnit)}</td>
                                            <td>{v.lPer100km}</td>
                                            <td className="font-medium">{fmtDec(v.fuel)}</td>
                                            <td className="font-medium">{fmtNum(v.cost)}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                    {(fuelQ.data?.byVehicle ?? []).length > 1 && (
                                        <tfoot>
                                        <tr className="font-semibold bg-gray-50">
                                            <td colSpan={3}>الإجمالي</td>
                                            <td>{formatDistance(fuelQ.data?.meta?.totalDistance ?? 0, distanceUnit)}</td>
                                            <td>{fmtDec(fuelQ.data?.meta?.avgLPer100km)}</td>
                                            <td>{fmtDec(fuelQ.data?.meta?.totalFuel)}</td>
                                            <td>{fmtNum(fuelQ.data?.meta?.totalCost)}</td>
                                        </tr>
                                        </tfoot>
                                    )}
                                </table>
                            </div>
                        </Panel>

                        <p className="text-xs text-gray-500 flex items-start gap-2">
                            <ExclamationTriangleIcon className="w-4 h-4 shrink-0 mt-0.5 text-warning-500"/>
                            أرقام تقديرية: تُحسب من المسافة المقطوعة الفعلية مضروبة في معدل استهلاك متوسط، وليست
                            قراءة وقود حقيقية. أجهزة FMC130 كما هي مركّبة لا ترسل مستوى الوقود، لذا يتوقّف قياس
                            الاستهلاك الفعلي على توصيل حساس وقود أو قراءة CAN‑bus. اضبط المعدل أعلاه ليعكس واقع
                            أسطولك كلما توفّرت أرقام تعبئة فعلية.
                        </p>
                    </>
                )}

                {/* ── Maintenance ──────────────────────────────────────── */}
                {selectedReport === 'maintenance' && (
                    <>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            {[
                                { l: 'متأخرة', v: maintenanceQ.data?.meta?.overdue ?? 0,
                                    c: 'text-emergency-600', b: 'border-r-emergency-500' },
                                { l: 'قريبة (خلال 30 يوم)', v: maintenanceQ.data?.meta?.dueSoon ?? 0,
                                    c: 'text-warning-600', b: 'border-r-warning-500' },
                                { l: 'جيدة', v: maintenanceQ.data?.meta?.ok ?? 0,
                                    c: 'text-success-600', b: 'border-r-success-500' },
                                { l: 'غير مجدولة', v: maintenanceQ.data?.meta?.unscheduled ?? 0,
                                    c: 'text-gray-600', b: 'border-r-gray-400' },
                            ].map((s) => (
                                <div key={s.l} className={`card border-r-4 ${s.b}`}>
                                    <div className="card-body">
                                        <p className="text-gray-500 text-xs sm:text-sm">{s.l}</p>
                                        <p className={`text-2xl font-bold ${s.c}`}>{s.v}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <Panel title="جدول الصيانة" height={200}
                               icon={<WrenchScrewdriverIcon className="w-5 h-5 text-gray-500"/>}
                               subtitle="مبني على تواريخ الصيانة المسجلة، والعدّاد = المسافة المسجّلة للمركبة + ما قطعته بعدها"
                               loading={maintenanceQ.isLoading} error={maintenanceQ.error}
                               empty={!(maintenanceQ.data?.items ?? []).length}>
                            <div className="overflow-x-auto">
                                <table className="table">
                                    <thead>
                                    <tr>
                                        <th>المركبة</th><th>النوع</th><th>آخر صيانة</th>
                                        <th>الصيانة القادمة</th><th>المتبقي</th><th>العداد</th>
                                        <th>تنبيهات صحية</th><th>الحالة</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {(maintenanceQ.data?.items ?? []).map((m: any) => (
                                        <tr key={m.id}>
                                            <td className="font-medium"><PlateLink id={m.id} plate={m.vehicle} /></td>
                                            <td>{m.vehicleType}</td>
                                            <td dir="ltr" className="text-sm">
                                                {m.lastMaintenanceDate
                                                    ? dayjs(m.lastMaintenanceDate).format('YYYY-MM-DD') : '—'}</td>
                                            <td dir="ltr" className="text-sm">
                                                {m.nextMaintenanceDate
                                                    ? dayjs(m.nextMaintenanceDate).format('YYYY-MM-DD') : '—'}</td>
                                            <td>{m.daysRemaining === null ? '—'
                                                : m.daysRemaining < 0
                                                    ? `متأخر ${Math.abs(m.daysRemaining)} يوم`
                                                    : `${m.daysRemaining} يوم`}</td>
                                            <td>
                                                {m.odometerKm > 0
                                                    ? formatDistance(m.odometerKm, distanceUnit) : '—'}
                                                {/* The recorded reading it was built from, so the
                                                    total can be checked against what was entered. */}
                                                {m.initialOdometerKm != null && (
                                                    <span className="block text-xs text-gray-400">
                                                        مسجّلة: {formatDistance(m.initialOdometerKm, distanceUnit)}
                                                    </span>
                                                )}
                                            </td>
                                            <td>{m.healthAlerts}</td>
                                            <td>
                                                <span className={clsx('badge',
                                                    m.status === 'overdue' ? 'badge-error'
                                                        : m.status === 'due_soon' ? 'badge-warning'
                                                            : m.status === 'ok' ? 'badge-success'
                                                                : 'badge-info')}>
                                                    {m.statusAr}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        </Panel>

                        {(maintenanceQ.data?.meta?.unscheduled ?? 0) > 0 && (
                            <p className="text-xs text-gray-500 flex items-start gap-2">
                                <ExclamationTriangleIcon className="w-4 h-4 shrink-0 mt-0.5 text-warning-500"/>
                                {maintenanceQ.data?.meta?.unscheduled} مركبة بلا تاريخ صيانة مسجّل. أضِف تاريخ
                                الصيانة القادمة من صفحة المركبات لتظهر في الجدولة.
                            </p>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
